/**
 * المرحلة 1.35 — any voucher, reopened from the desk's list: the centre's own
 * paper (VoucherSheet) to print again, the ready WhatsApp link to send it to
 * the guardian, and «مرتجع» to hand services back with a reason.
 *
 * The WhatsApp link is minted on the first tap (a new soft-copy link revokes
 * the voucher's previous one) and then stays on the window: one more tap
 * opens WhatsApp Web on the guardian's chat with the message written.
 */

import { useRef, useState } from 'react';
import { Badge, Button } from '../../components/common';
import { quickDeskApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { useI18n } from '../../i18n/I18nProvider';
import { centerPolicy } from '../../domain/centerPolicy';
import { activeLocale } from '../../domain/locale';
import { mayRefund } from '../../domain/refund';
import { sheetOf } from '../../domain/voucher';
import { whatsappHref } from '../../domain/whatsapp';
import { WhatsAppLink } from '../../components/common/WhatsAppLink';
import { printArea } from '../hospitalShared/labels';
import { VoucherSheet } from './VoucherSheet';
import { RefundDialog } from './RefundDialog';

export function VoucherPreview({ receipt, onChanged }) {
  const { t } = useI18n();
  const { error: toastError } = useNotifications();
  const { institutionName, institutionLogo, institutionLogoBackdrop, locale, can } = usePermissions();
  const policy = centerPolicy(locale).quickReception;
  const sheetRef = useRef(null);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const sheet = sheetOf(receipt, {
    institution: { name: institutionName, logo: institutionLogo, logoBackdrop: institutionLogoBackdrop },
    currency: activeLocale().country.currency,
  });
  const refunded = Array.isArray(receipt.refunds) && receipt.refunds.length > 0;
  const canRefund = receipt.status !== 'void' && !receipt.stayId && mayRefund(policy, can);

  const send = async () => {
    if (link) return;
    // Opened in the click itself, so the browser does not block it.
    const win = window.open('', 'capsule-whatsapp');
    setBusy(true);
    try {
      const made = await quickDeskApi.link(receipt.id);
      setLink(made);
      if (win) win.location.href = whatsappHref(made.whatsappUrl);
    } catch (err) {
      if (win) win.close();
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="voucher-preview">
      <div className="flex flex-wrap items-center gap-2">
        {refunded && <Badge className="border-rose-500/40 bg-rose-500/10 text-rose-200">{t('refund.badge')}</Badge>}
        <Button icon="printer" onClick={() => printArea(sheetRef.current, { title: receipt.number || t('quick.title') })}>
          {t('quick.printAgain')}
        </Button>
        {policy.softCopy === 'link' && receipt.status !== 'void' && !link && (
          <Button variant="success" icon="message-circle" loading={busy} onClick={send} data-testid="voucher-whatsapp">
            {t('quick.whatsapp')}
          </Button>
        )}
        {canRefund && (
          <Button variant="danger" icon="undo-2" onClick={() => setRefunding(true)} data-testid="voucher-refund">
            {t('refund.open')}
          </Button>
        )}
      </div>
      {link && <WhatsAppLink url={link.whatsappUrl} label={t('whatsapp.voucher')} preview testId="voucher-whatsapp-link" />}
      <div>
        <VoucherSheet ref={sheetRef} sheet={sheet} qrSvg={link && link.qrSvg} />
      </div>
      <RefundDialog
        receipt={receipt}
        open={refunding}
        onClose={() => setRefunding(false)}
        onDone={(updated) => {
          setRefunding(false);
          onChanged(updated);
        }}
      />
    </div>
  );
}

export default VoucherPreview;
